// Copyright (c) 2022 by Move Closer

import { computed, SetupContext, toRef } from '@vue/composition-api'
import { VueConstructor } from 'vue'

import { ComponentObjectPropsOptions } from '../../vue-api'
import { FormControlModelType } from '../../abstract/general'
import {
  getAbstractCheckControlProps,
  useCheckControl
} from '../../abstract/components/CheckControl/CheckControl.hooks'
import { useErrors } from '../../composables'

import { CardCheckProps, UseCardCheckProvides } from './CardCheck.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const cardCheckProps: ComponentObjectPropsOptions<CardCheckProps> = {
  ...getAbstractCheckControlProps<FormControlModelType>(),

  /**
   * @see CardCheckProps.hasBorder
   */
  hasBorder: {
    type: Boolean,
    required: false,
    default: true
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 */
export const useCardCheck = (
  props: CardCheckProps,
  ctx: SetupContext
): UseCardCheckProvides<string | number | boolean> => {
  const { clearErrors, hasErrors } = useErrors(toRef(props, 'errors'), ctx.emit)
  const { checked, checkType, checkValue } = useCheckControl(props, ctx.emit, clearErrors)

  const isActive = computed<boolean>(() => {
    if (Array.isArray(checked.value) && checked.value.length > 0) {
      return checked.value.includes(checkValue.value)
    }

    if (checked.value && typeof checked.value === 'object') {
      return Object.prototype.hasOwnProperty.call(checked.value, String(checkValue.value))
    }

    return checkValue.value === checked.value
  })

  const isLabelSrOnly = computed<boolean>(() => {
    return !!Object.keys(ctx.slots).length
  })

  const cardComponent = computed<VueConstructor | string>(() => {
    return ctx.slots.default && ctx.slots.default.length > 0 ? 'div' : 'label'
  })

  const labelComponent = computed<VueConstructor | string>(() => {
    return ctx.slots.default ? 'label' : 'span'
  })

  return { cardComponent, checked, checkType, checkValue, hasErrors, isActive, isLabelSrOnly, labelComponent }
}
